import { getUserLocale } from './datetime';

/**
 * 숫자/통화 표시 헬퍼 — 사용자 locale 따라 separator·기호 변동.
 *
 * formatNumber: 일반 숫자 ('12,345' 한국어, '12,345' 영어 모두 비슷, 스페인어는 '12.345')
 * formatWon: '12,345원' 한국어 통화 표기 (현재 비즈니스 KRW 단일)
 * formatCurrency: Intl 표준 통화 표기 ('₩12,345', '$12.30' 등). 다국 통화 시.
 */

const DEFAULT_LOCALE = 'ko-KR';

export function formatNumber(
 n: number | string | null | undefined,
 opts: Intl.NumberFormatOptions = {},
): string {
 if (n == null) return '-';
 const num = typeof n === 'string' ? Number(n) : n;
 if (!Number.isFinite(num)) return '-';
 try {
  return new Intl.NumberFormat(getUserLocale() || DEFAULT_LOCALE, opts).format(num);
 } catch {
  return String(num);
 }
}

/** '12,300원' — KRW 한국어 표기. 비즈니스 단일 통화일 때 권장. */
export function formatWon(n: number | string | null | undefined): string {
 if (n == null) return '-';
 const num = typeof n === 'string' ? Number(n) : n;
 if (!Number.isFinite(num)) return '-';
 return `${formatNumber(Math.round(num))}원`;
}

/**
 * 회당 단가 환산 정보 — 협상/마켓 카드 hover tooltip 용.
 *
 * 가정: 8시간 운영 × 10슬롯 × 15초 광고 = 일 1,920회.
 *  - 1슬롯당 airtime: 8h / 10 = 48분 = 2,880초
 *  - 1슬롯당 일일 송출: 2,880 / 15 = 192회
 *  - 10슬롯 합계: 1,920회
 * 광고 길이 / 운영 시간 / 슬롯 수가 다른 패밀리는 비례 환산이 필요.
 */
const DAILY_OPS_HOURS = 8;
const TOTAL_SLOTS = 10;
const AD_DURATION_SEC = 15;
const DAILY_IMPRESSIONS_BASELINE =
  Math.floor(DAILY_OPS_HOURS * 3600 / TOTAL_SLOTS / AD_DURATION_SEC) * TOTAL_SLOTS;

export interface PriceConversion {
  perImpression: number;     // CPI
  cpm: number;               // 1,000회 단가
  dailyImpressions: number;  // baseline 가정 하 일 송출 횟수
  dailyTotal: number;        // baseline 가정 하 일 매출
}

export function priceConversion(pricePerImpression: number | string | null | undefined): PriceConversion | null {
  const p = typeof pricePerImpression === 'string' ? Number(pricePerImpression) : pricePerImpression;
  if (p == null || !Number.isFinite(p)) return null;
  return {
    perImpression: p,
    cpm: p * 1000,
    dailyImpressions: DAILY_IMPRESSIONS_BASELINE,
    dailyTotal: p * DAILY_IMPRESSIONS_BASELINE,
  };
}

/** title 속성용 평문 — 줄바꿈 \n 으로 구분. */
export function priceTooltipText(pricePerImpression: number | string | null | undefined): string {
  const c = priceConversion(pricePerImpression);
  if (!c) return '';
  return [
    `회당 (CPI): ${formatWon(c.perImpression)}`,
    `1,000회 (CPM): ${formatWon(c.cpm)}`,
    `일 예상 (${DAILY_OPS_HOURS}시간 × ${TOTAL_SLOTS}슬롯, ${AD_DURATION_SEC}초 광고): ${c.dailyImpressions.toLocaleString()}회 = ${formatWon(c.dailyTotal)}`,
  ].join('\n');
}

/** Intl 표준 통화 — '₩12,300', 'US$12.30' 등. 다통화 운영 합류 시 권장. */
export function formatCurrency(
 n: number | string | null | undefined,
 currency: string = 'KRW',
): string {
 if (n == null) return '-';
 const num = typeof n === 'string' ? Number(n) : n;
 if (!Number.isFinite(num)) return '-';
 try {
  return new Intl.NumberFormat(getUserLocale() || DEFAULT_LOCALE, {
   style: 'currency', currency, currencyDisplay: 'symbol',
  }).format(num);
 } catch {
  return String(num);
 }
}
